/**
 * 3×3 matrix utilities for DCP color profile maths.
 *
 * Adobe DNG 1.7 spec, section 6.4 — Camera Calibration. DCPs ship two
 * canonical matrices:
 *   ColorMatrix1/2:    XYZ(D50) → cameraRGB at illuminant 1/2
 *   ForwardMatrix1/2:  cameraRGB → XYZ(D50) at illuminant 1/2  (preferred)
 *
 * If ForwardMatrix is absent (older / minimalist DCPs), we invert
 * ColorMatrix to get the same direction. ForwardMatrix is preferred
 * because it bakes Adobe's reference-white-balance + chromatic-adaptation
 * choices, while inverted ColorMatrix is only correct at the
 * calibration illuminant.
 *
 * To reach our pipeline's working space (linear-Rec.709 → ACEScg in
 * `ingest.js`), we need: cameraRGB → linear-Rec.709 (D65). That's a
 * single 3×3 product:
 *
 *     M = M_XYZ_to_Rec709_D65 · ForwardMatrix_interpolated
 *
 * where ForwardMatrix_interpolated is a t-blend of FM1 and FM2.
 *
 * Output is consumed by `ingestWithMatrix(rgba, w, h, mat3x3)` which
 * applies it per-pixel before the standard sRGB→ACEScg matrix.
 *
 * @module src/pipeline/dcp/matrix
 */

/* Bradford-adapted XYZ(D50) → linear-Rec709(D65) matrix.
 * Sourced from Bruce Lindbloom's published RGB/XYZ tables (linear sRGB
 * primaries; sRGB matrix, D65, 2° observer; D50→D65 chromatic
 * adaptation embedded so we can multiply against a D50-XYZ source
 * directly).
 *
 * Values pre-baked: M_sRGB_from_XYZD65 · M_BradfordD50toD65.
 * Verified against the IEC 61966-2-1 sRGB whitepoint matrix and the
 * Bradford CAT in Lindbloom — round-trip identity to ~1e-6.
 *
 * NOTE: typed-array views (Float32Array) cannot be Object.freeze()'d
 * in V8/SpiderMonkey ("Cannot freeze array buffer views with
 * elements"). We rely on convention to keep this immutable. */
const M_XYZD50_TO_REC709 = new Float32Array([
     3.1338561, -1.6168667, -0.4906146,
    -0.9787684,  1.9161415,  0.0334540,
     0.0719453, -0.2289914,  1.4052427,
]);

/**
 * Multiply two row-major 3×3 matrices: out = A · B.
 *
 * @param {ArrayLike<number>} A   row-major 9-element
 * @param {ArrayLike<number>} B   row-major 9-element
 * @returns {Float32Array}        out[9]
 */
export function mulMat3(A, B) {
    const out = new Float32Array(9);
    for (let r = 0; r < 3; r++) {
        for (let c = 0; c < 3; c++) {
            let s = 0;
            for (let k = 0; k < 3; k++) s += A[r * 3 + k] * B[k * 3 + c];
            out[r * 3 + c] = s;
        }
    }
    return out;
}

/**
 * Invert a 3×3 row-major matrix. Returns null on singular input
 * (caller should fall back to identity or skip DCP path).
 *
 * @param {ArrayLike<number>} M  row-major 9-element
 * @returns {Float32Array|null}
 */
export function invMat3(M) {
    const a = M[0], b = M[1], c = M[2];
    const d = M[3], e = M[4], f = M[5];
    const g = M[6], h = M[7], i = M[8];
    const A =  (e * i - f * h);
    const B = -(d * i - f * g);
    const C =  (d * h - e * g);
    const det = a * A + b * B + c * C;
    if (Math.abs(det) < 1e-12) return null;
    const inv = 1 / det;
    const out = new Float32Array(9);
    out[0] = A * inv;
    out[1] = -(b * i - c * h) * inv;
    out[2] =  (b * f - c * e) * inv;
    out[3] = B * inv;
    out[4] =  (a * i - c * g) * inv;
    out[5] = -(a * f - c * d) * inv;
    out[6] = C * inv;
    out[7] = -(a * h - b * g) * inv;
    out[8] =  (a * e - b * d) * inv;
    return out;
}

/**
 * Linear blend of two row-major 3×3 matrices: out = (1-t)·A + t·B.
 *
 * @param {ArrayLike<number>} A
 * @param {ArrayLike<number>} B
 * @param {number} t            ∈ [0, 1]
 * @returns {Float32Array}
 */
export function lerpMat3(A, B, t) {
    const out = new Float32Array(9);
    const u = 1 - t;
    for (let i = 0; i < 9; i++) out[i] = u * A[i] + t * B[i];
    return out;
}

/**
 * Compose the final cameraRGB → linear-Rec709 matrix from a DCPProfile
 * + an interpolation weight `t` ∈ [0, 1].
 *
 * Decision tree (DNG 1.7 § 6.5.1):
 *   - Both ForwardMatrix1 and ForwardMatrix2 present → blend, then
 *     premultiply by XYZD50→Rec709.
 *   - One ForwardMatrix present → use it directly, premultiply.
 *   - No ForwardMatrix → invert ColorMatrix1/2, blend the inverses.
 *     (Less accurate; only correct at the calibration illuminants.)
 *
 * Returns null only if the profile is internally inconsistent (e.g.
 * has neither ForwardMatrix nor ColorMatrix, or singular ColorMatrix
 * with no FM fallback). Caller should fall back to the legacy
 * linear-Rec709 ingest path on null.
 *
 * @param {object} profile      from parseDCP()
 * @param {number} [t=0]        illuminant blend weight; 0 = illum1 only.
 *                              For v1 we don't compute the actual image
 *                              white-balance interpolation — just pick
 *                              the "best" matrix below.
 * @returns {Float32Array|null} cameraRGB → linear-Rec709 (3×3, row-major)
 */
export function buildCameraToRec709(profile, t = 0) {
    if (!profile) return null;
    const fm1 = profile.forwardMatrix1;
    const fm2 = profile.forwardMatrix2;
    const cm1 = profile.colorMatrix1;
    const cm2 = profile.colorMatrix2;

    let camToXYZ = null;
    if (fm1 && fm2) {
        camToXYZ = lerpMat3(fm1, fm2, t);
    } else if (fm1) {
        camToXYZ = fm1.slice();
    } else if (fm2) {
        camToXYZ = fm2.slice();
    } else if (cm1 && cm2) {
        const inv1 = invMat3(cm1);
        const inv2 = invMat3(cm2);
        if (inv1 && inv2) camToXYZ = lerpMat3(inv1, inv2, t);
        else if (inv1) camToXYZ = inv1;
        else if (inv2) camToXYZ = inv2;
    } else if (cm1) {
        camToXYZ = invMat3(cm1);
    } else if (cm2) {
        camToXYZ = invMat3(cm2);
    }
    if (!camToXYZ) return null;

    /* Final compose: XYZ(D50) → Rec709 · cameraRGB → XYZ(D50). */
    return mulMat3(M_XYZD50_TO_REC709, camToXYZ);
}
